import DocViewer from '@cyntler/react-doc-viewer';
import classNames from 'classnames';
import { safeDocViewerRenderers } from '@/modules/admin-dashboard/helpers/docViewer.ts';
import { Button, Spinner } from 'ui';
import { PdfViewer } from 'ui/PDFViewer';
import { useIsMobile } from 'ui/hooks';
import { DownloadGreyIcon } from 'ui/icons';
import '@cyntler/react-doc-viewer/dist/index.css';

interface ContractByIDPreviewPanelProps {
  fileUrl?: string;
  fileName?: string;
}

export const ContractByIDPreviewPanel = ({ fileUrl, fileName }: ContractByIDPreviewPanelProps) => {
  const { isMobile } = useIsMobile();

  if (!fileUrl) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center rounded border border-gray-200 bg-white text-sm text-gray-500">
        No contract file uploaded
      </div>
    );
  }

  const format = getFileFormat(fileName);
  const isPdf = format === 'pdf';
  const isImage = ['webp', 'bmp', 'gif', 'jpg', 'jpeg', 'png'].includes(format);

  if (isPdf) {
    return (
      <PdfViewer
        enableZoom
        src={fileUrl}
        defaultScale={isMobile ? 0.8 : 1}
        className="h-[calc(100vh-71px)] w-full"
        hidePageShadow
      />
    );
  }

  if (isImage) {
    return <img alt={fileName || ''} src={fileUrl} className="max-h-full w-full object-contain" />;
  }

  return (
    <DocViewer
      config={{
        header: { disableHeader: true },
        noRenderer: {
          overrideComponent: () => (
            <div className="flex min-h-[420px] h-[calc(100vh-71px)] items-center justify-center px-6">
              <div className="flex max-w-[360px] flex-col items-center text-center">
                <div className="text-sm font-medium text-gray-900">Preview is unavailable</div>
                <div className="mt-2 text-sm leading-6 text-gray-500">
                  This file type cannot be displayed in the browser.
                </div>
                <a
                  href={fileUrl}
                  download={fileName}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-5 inline-flex"
                >
                  <Button variant="secondary" className="w-auto gap-2 px-4">
                    <img src={DownloadGreyIcon} alt="" className="h-5 w-5" />
                    Download file
                  </Button>
                </a>
              </div>
            </div>
          ),
        },
        loadingRenderer: {
          overrideComponent: () => (
            <div className="flex h-[240px] items-center justify-center">
              <Spinner />
            </div>
          ),
        },
      }}
      pluginRenderers={safeDocViewerRenderers}
      documents={[{ fileType: format, uri: fileUrl }]}
      className={classNames(
        '[&_[id="proxy-renderer"]]:h-full [&_[id="proxy-renderer"]]:overflow-y-auto',
        '[&_[id="msdoc-renderer"]]:h-full [&_[id="msdoc-iframe"]]:h-full',
        '[&_[id="image-renderer"]]:bg-none',
        'h-[70dvh] min-h-[520px] lg:h-[calc(100vh-71px)] [&>div]:h-full [&>div>div]:h-full',
      )}
    />
  );
};

const getFileFormat = (fileName?: string) => {
  const extension = fileName?.split('.').pop()?.toLowerCase();
  return extension || 'pdf';
};
